/**
 * سربرگِ «بازخوردها» — جایی که حرفِ کاربر خوانده می‌شود.
 *
 * ═══ چرا وجود دارد ═══
 *
 * فرمِ بازخورد از روزِ اول کار می‌کرد و متن را در `client_errors` می‌نوشت،
 * ولی هیچ صفحه‌ای نداشت. یعنی هر کسی که چیزی نوشته بود، عملاً در خالی حرف
 * زده بود. این سربرگ و خبرِ تلگرام (تریگرِ مایگریشن 0113) با هم آن حلقه را
 * می‌بندند: تلگرام می‌گوید «همین حالا یکی چیزی گفت»، این‌جا جای خواندن و
 * دنبال کردنش است.
 *
 * ═══ چرا کنارِ متن، همیشه چیزِ فنی هم هست ═══
 *
 * «یه چیزی خرابه» بدونِ زمینه یعنی یک ساعت حدس زدن. پس هر ردیف سه چیزِ
 * دیگر هم دارد: نسخه و پلتفرم (خودکار)، لاگِ تشخیصیِ دستگاه (اگر کاربر
 * تیکش را زده)، و شمارشِ خطاهای خودکارِ همان کاربر در نیم‌ساعتِ پیش از
 * گزارش. آخری معمولاً همان سرنخ است.
 *
 * ⚠ هیچ ستونِ «خوانده شد» ندارد و عمدی است: خبرِ تلگرام همان لحظه می‌رسد و
 * صفِ کار همان‌جاست. یک وضعیتِ دوم روی همان ردیف یعنی دو جا برای دنبال
 * کردنِ یک چیز، که همیشه یکی‌شان کهنه می‌ماند.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo, type FeedbackItem } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import {
  FEEDBACK_ICON, FEEDBACK_LABEL, diagHighlights, feedbackKind,
} from '@/core/lib/feedback';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';

/** آدرسِ فرمی که این ردیف‌ها از آن می‌آیند — برای وقتی که آدم می‌خواهد خودش تست کند. */
const FORM_HELP = 'کاربر از تنظیمات → ارسال بازخورد می‌نویسد (هم در اپ، هم در سایت).';

const who = (f: { who: string | null; username: string | null }): string =>
  [f.who, f.username ? `@${f.username}` : null].filter(Boolean).join(' ') || 'بی‌نام';

const device = (f: { platform: string | null; app_version: string | null }): string =>
  [f.platform, f.app_version ? `نسخه ${f.app_version}` : null].filter(Boolean).join(' · ') || '—';

/* ─────────────────────────── فهرست ─────────────────────────── */

function Row({ f, onOpen }: { f: FeedbackItem; onOpen: () => void }) {
  const kind = feedbackKind(`feedback.${f.kind}`);
  return (
    <div className="dup">
      <div className="dup-head">
        <span className="chip">{FEEDBACK_ICON[kind]} {FEEDBACK_LABEL[kind]}</span>
        <span className="dim">{formatDateTime(f.at)}</span>
        <span className="spacer" style={{ flex: 1 }} />
        <button type="button" className="linkbtn" onClick={onOpen}>باز کن</button>
      </div>

      {/* متنِ کاربر، دست‌نخورده و بدونِ بریدن: کلِ دلیلِ وجودِ این صفحه همین است. */}
      <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>{f.text}</p>

      <div className="chiprow">
        <span className="dim">{who(f)}</span>
        <span className="dim ltr">{device(f)}</span>
        {f.diag_id ? <span className="chip">🧪 لاگِ تشخیصی</span> : null}
        {f.errors_near > 0 ? (
          <span className="chip warn" title="خطاهای خودکارِ همان کاربر، از نیم‌ساعت پیش از این گزارش تا پنج دقیقه بعدش">
            {fmt(f.errors_near)} خطای هم‌زمان
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────────────────── یک بازخورد ─────────────────────────── */

function Detail({ id, onBack }: { id: number; onBack: () => void }) {
  const q = useQuery({
    queryKey: ['feedbackDetail', id],
    queryFn: () => opsRepo.feedbackDetail(id),
  });

  if (q.isLoading) return <p className="dim">…</p>;
  if (q.error) return <div className="card"><p className="err">{String(q.error)}</p></div>;

  const d = q.data!;
  const kind = feedbackKind(`feedback.${d.kind}`);
  const log = d.diag_log;
  const marks = diagHighlights(log?.lines);

  return (
    <>
      <div className="topbar" style={{ borderBottom: 'none' }}>
        <button type="button" className="tab" onClick={onBack}>← برگشت</button>
      </div>

      <div className="card">
        <div className="chiprow">
          <span className="chip on">{FEEDBACK_ICON[kind]} {FEEDBACK_LABEL[kind]}</span>
          <span className="dim">{formatDateTime(d.at)}</span>
        </div>

        <p style={{ whiteSpace: 'pre-wrap', margin: '10px 0', fontSize: 15 }}>{d.text}</p>

        <table className="tbl">
          <tbody>
            <tr><th>کی</th><td>{who(d)}</td></tr>
            <tr><th>دستگاه</th><td className="ltr">{device(d)}</td></tr>
            <tr><th>زبانِ اپ</th><td className="ltr">{d.locale ?? '—'}</td></tr>
          </tbody>
        </table>
      </div>

      {/* ═══ لاگِ تشخیصی ═══ */}
      <div className="card">
        <h2>🧪 لاگِ تشخیصیِ دستگاه</h2>

        {!d.diag ? (
          <p className="dim">
            کاربر نفرستاده — یا نوعِ بازخورد «باگ» نبوده، یا تیکش را برداشته.
          </p>
        ) : !log ? (
          // حالتی که با دو کلیدِ جدا دیدنی شد: شناسه روی ردیفِ بازخورد هست
          // ولی ردیفِ لاگ نیست. یعنی فرستادنش شکست خورده و اپ (درست) به روی
          // خودش نیاورده.
          <p className="err">شناسه‌اش هست ({d.diag}) ولی خودِ لاگ به دیتابیس نرسیده.</p>
        ) : (
          <>
            <p className="dim">
              {fmt(marks.rows)} سطر · {log.seen ?? '?'} رویداد در کلِ نشست ·{' '}
              {formatDateTime(log.at)}
              {log.stopped ? ` · لاگ خودش را خاموش کرده: ${log.stopped}` : ''}
            </p>

            {marks.errors.length ? (
              <>
                <h3 style={{ marginTop: 10 }}>خطاها</h3>
                <pre className="mono raw-json">{marks.errors.join('\n')}</pre>
              </>
            ) : null}

            {marks.slow.length ? (
              <>
                <h3 style={{ marginTop: 10 }}>
                  <Hint text="کندترین‌ها" help="هر چیزی که بیشتر از یک ثانیه طول کشیده، از کندترین به بالا" />
                </h3>
                <pre className="mono raw-json">{marks.slow.join('\n')}</pre>
              </>
            ) : null}

            {!marks.errors.length && !marks.slow.length ? (
              <p className="dim">نه خطایی، نه چیزی کندتر از یک ثانیه.</p>
            ) : null}

            <details style={{ marginTop: 10 }}>
              <summary className="linkbtn">کلِ لاگ</summary>
              <pre className="mono raw-json" style={{ maxHeight: 460, overflow: 'auto' }}>
                {log.lines}
              </pre>
            </details>
          </>
        )}
      </div>

      {/* ═══ خطاهای خودکارِ همان بازه ═══ */}
      <div className="card">
        <h2>
          <Hint text="خطاهای خودکارِ همان کاربر"
            help="از نیم‌ساعت پیش از این گزارش تا پنج دقیقه بعدش — همان بازه‌ای که مشکل در آن افتاده" />
        </h2>
        {d.errors.length === 0 ? (
          <p className="dim">هیچ خطای خودکاری در آن بازه ثبت نشده.</p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr><th>کِی</th><th>کجا</th><th>چه شد</th></tr>
              </thead>
              <tbody>
                {d.errors.map((e, i) => (
                  <tr key={i}>
                    <td className="num">{formatDateTime(e.at)}</td>
                    <td className="mono">{e.where_at}</td>
                    <td>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────── خودِ سربرگ ─────────────────────────── */

type Filter = 'all' | 'bug' | 'idea' | 'other';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'همه' },
  { id: 'bug', label: `${FEEDBACK_ICON.bug} ${FEEDBACK_LABEL.bug}` },
  { id: 'idea', label: `${FEEDBACK_ICON.idea} ${FEEDBACK_LABEL.idea}` },
  { id: 'other', label: `${FEEDBACK_ICON.other} ${FEEDBACK_LABEL.other}` },
];

export function FeedbackTab() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  const q = useQuery({ queryKey: ['feedback'], queryFn: () => opsRepo.feedback() });

  if (openId) return <Detail id={openId} onBack={() => setOpenId(null)} />;

  if (q.isLoading) return <p className="dim">…</p>;
  if (q.error) return <div className="card"><p className="err">{String(q.error)}</p></div>;

  const d = q.data!;
  const rows = filter === 'all'
    ? d.items
    : d.items.filter((f) => feedbackKind(`feedback.${f.kind}`) === filter);
  // شمارش همیشه رویِ کلِ فهرست است و نه رویِ چیزی که الان فیلتر شده — وگرنه
  // عددِ رویِ هر قرص با انتخابِ همان قرص عوض می‌شد.
  const count = (k: Filter) => k === 'all'
    ? d.items.length
    : d.items.filter((f) => feedbackKind(`feedback.${f.kind}`) === k).length;

  return (
    <>
      <div className="grid n3" style={{ marginTop: 12 }}>
        <div className="card stat">
          <div className="n">{fmt(d.total)}</div>
          <div className="k"><Hint text="بازخوردِ ثبت‌شده" help={FORM_HELP} /></div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(count('bug'))}</div>
          <div className="k">«یک چیزی خراب است»</div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(d.items.filter((f) => f.diag_id).length)}</div>
          <div className="k">با لاگِ تشخیصی</div>
        </div>
      </div>

      <div className="card">
        <div className="chiprow">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`chip${filter === f.id ? ' on' : ''}`}
              onClick={() => setFilter(f.id)}>
              {f.label} {fmt(count(f.id))}
            </button>
          ))}
        </div>

        {d.total === 0 ? (
          // خالی بودن هم یک یافته است، نه یک صفحه‌ی خراب — پس می‌گوید از کجا
          // پر می‌شود و چطور امتحانش کنیم.
          <p className="dim" style={{ marginTop: 10 }}>
            هنوز هیچ بازخوردی نیامده. {FORM_HELP} هر بازخوردِ تازه همان لحظه به تلگرام هم می‌رود.
          </p>
        ) : rows.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>از این نوع چیزی نیست.</p>
        ) : (
          <div style={{ marginTop: 6 }}>
            {rows.map((f) => <Row key={f.id} f={f} onOpen={() => setOpenId(f.id)} />)}
          </div>
        )}
      </div>
    </>
  );
}
